import "../styles/guideSheet.css";
import { icon } from "../iconsKit";
import { portraitUrl } from "../portraits";
import { guide } from "./guide";
import { bramStill } from "./bram/bramView";
import type { GuideApi, GuideCreateOptions, GuideTopicId } from "./guide";
import { GROUP_TITLES, GuideTopic, PRACTICE_LEVEL, TopicGroup, TopicState, allGuideTopics, topicState } from "./topics";
import { kickerHtml, sheetHeadHtml, tagHtml } from "../components";

export interface GuideSheetDeps {
  readonly highestCleared: number;
  readonly unlocks: GuideCreateOptions["unlocks"];
  /** "Practice battle with me": the caller clears the walkthrough (startPractice) and starts level 1. */
  readonly onPractice: () => void;
}

/** localStorage key: the topics this device has opened in the sheet (their NEW goes). A per-device nicety only. */
const VIEWED_KEY = "highmarch.guideTopicsViewed";
const GROUPS: readonly TopicGroup[] = ["basics", "enemies", "towers", "castle"];
/** Row icon size in its 30 px well, and the row's chevron. */
const ROW_ICON_PX = 18;
const CHEV_PX = 12;
/** The captain's portrait in the header (guideSheet.css). */
const HEADER_PORTRAIT_PX = 70;

function loadViewed(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(VIEWED_KEY) ?? "[]") as string[]); } catch { return new Set(); }
}
function saveViewed(v: ReadonlySet<string>): void {
  try { localStorage.setItem(VIEWED_KEY, JSON.stringify([...v])); } catch { /* private mode: NEW simply stays */ }
}

/** A row's well: the topic's portrait when it has one (enemies, soldiers), else its kit icon. */
function rowIcon(t: GuideTopic, state: TopicState): string {
  if (state === "locked") return t.group === "enemies" ? "?" : icon("lock", ROW_ICON_PX);
  const art = t.art.portrait ? portraitUrl(t.art.portrait) : null;
  return art ? `<img decoding="async" src="${art}" alt="">` : icon(t.art.icon, ROW_ICON_PX);
}

function rowHtml(t: GuideTopic, state: TopicState): string {
  const label = state === "locked" && t.group === "enemies" ? "Not met yet" : t.label;
  const tail = state === "done" ? `<i class="ok">✓</i>` : state === "locked" ? "" : `${state === "new" ? tagHtml("new") : ""}${icon("chev", CHEV_PX)}`;
  // "is-locked", not "locked": the kit's generic .locked (tokens.css) greys and veils a whole block.
  return `<button type="button" class="grow ${state === "locked" ? "is-locked" : state}" data-topic="${t.id}" ${state === "locked" ? "disabled" : ""}><span class="ic">${rowIcon(t, state)}</span><b>${label}</b>${tail}</button>`;
}

/**
 * More → Guide (DESIGN §3.6, mockup 6): the captain's header, "Practice battle with me" (level 1 with the walkthrough
 * on), the topics in three groups (tap → the captain replays its lines inside the sheet) and the tips toggle (which
 * never silences the warnings: GuideQueue.isSafety). A
 * parchment sheet under the hub's single-sheet rule (class `guide`: the More tab stays lit). Needs an installed guide.
 */
export function showGuideSheet(parent: HTMLElement, deps: GuideSheetDeps): HTMLElement | null {
  const g: GuideApi | null = guide();
  if (!g) return null;
  const sheet = document.createElement("div");
  sheet.className = "sheet panel guide";
  const viewed = loadViewed();
  const render = () => {
    const facts = { highestCleared: deps.highestCleared, isDone: (id: string) => g.isDone(id), unlocks: deps.unlocks, viewed };
    const groups = GROUPS.map((grp) => `${kickerHtml(GROUP_TITLES[grp])}<div class="ggrid">${allGuideTopics().filter((t) => t.group === grp).map((t) => rowHtml(t, topicState(t, facts))).join("")}</div>`).join("");
    sheet.innerHTML = `
      ${sheetHeadHtml({ title: "Captain's Guide", portrait: "", tint: "blue" })}
      <button type="button" class="grow hero practice"><span class="ic">${icon("play", ROW_ICON_PX)}</span><b>Practice battle with me</b><small>Level ${PRACTICE_LEVEL} ${icon("chev", 12)}</small></button>
      ${groups}
      <div class="grow tips"><span class="lbl"><b>Captain's tips during battles</b><small>Boss and flyer warnings still sound</small></span><button type="button" class="toggle ${g.tipsOff ? "" : "on"}" role="switch" aria-checked="${!g.tipsOff}" aria-label="Captain's tips during battles"><span class="knob"></span></button></div>`;
    sheet.querySelector(".sheethead .portrait")?.append(bramStill("neutral", HEADER_PORTRAIT_PX));
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
    sheet.querySelector<HTMLButtonElement>(".practice")!.onclick = () => { sheet.remove(); deps.onPractice(); };
    sheet.querySelector<HTMLButtonElement>(".tips .toggle")!.onclick = () => { g.setTipsOff(!g.tipsOff); render(); };
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".grow[data-topic]:not([disabled])")) b.onclick = () => replay(b.dataset.topic as GuideTopicId);
  };
  /** The captain goes over a topic as info cards inside the sheet (the controller draws them), then the list is back. */
  const replay = (topic: GuideTopicId) => {
    viewed.add(topic);
    saveViewed(viewed);
    const host = document.createElement("div");
    host.className = "g-replay";
    sheet.scrollTop = 0;
    sheet.classList.add("replaying");
    sheet.append(host);
    g.replayTopic(topic, host, () => { host.remove(); sheet.classList.remove("replaying"); if (sheet.isConnected) render(); });
  };
  render();
  parent.append(sheet);
  return sheet;
}
